/**
 * /prompt-playground — PUBLIC, anonymous-friendly multi-model fan-out.
 * ====================================================================
 * Paste a system + user prompt, pick 1-3 models (6 if signed in), hit Run.
 * Backend fans them out in parallel, returns side-by-side responses with
 * latency, tokens (in/out), and USD cost per model.
 *
 * Same wedge as OpenAI / Anthropic / Google playgrounds, but cross-provider
 * and with truthful $-comparison built in. Top-of-funnel lead magnet — the
 * "Save this prompt to my library" CTA gates on signup.
 *
 * Brand: slate-950, emerald accents, amber for the rate-limit warning.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import SEOHead from '../components/SEOHead';
import {
  ArrowLeft, Sparkles, Loader2, Play, Cpu, Zap, DollarSign,
  Clock, AlertTriangle, Bookmark, ArrowRight, Beaker,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCost = (n) => {
  if (!n) return '$0';
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(6)}`;
};

const ModelChip = ({ model, selected, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(model.id)}
    data-testid={`playground-model-${model.id}`}
    className={`
      group relative text-left px-3.5 py-3 rounded-lg border transition-all
      ${selected
        ? 'bg-emerald-500/15 border-emerald-500/60 ring-1 ring-emerald-400/40'
        : 'bg-slate-900 border-slate-800 hover:border-slate-700'}
    `}
  >
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold text-slate-100">{model.display_name}</span>
      {model.recommended && (
        <Badge className="text-[9px] bg-emerald-500/20 border-emerald-500/40 text-emerald-300 px-1.5 py-0">REC</Badge>
      )}
    </div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
      {model.provider} · {model.speed_tier}
    </div>
    <div className="text-[10px] text-slate-400 mt-1">
      in ${model.in_price_per_m}/M · out ${model.out_price_per_m}/M
    </div>
  </button>
);

const ResultCard = ({ r }) => {
  const errored = !!r.error;
  return (
    <Card className={`bg-slate-900 text-slate-100 border-slate-800 overflow-hidden ${errored ? 'border-rose-500/40' : ''}`} data-testid={`playground-result-${r.model}`}>
      <div className="px-4 py-2.5 border-b border-slate-800/80 flex items-center justify-between bg-slate-950">
        <div className="flex items-center gap-2">
          <Cpu className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-sm font-semibold text-slate-100">{r.model}</span>
          <Badge className="text-[9px] bg-slate-800 border-slate-700 text-slate-400 px-1.5 py-0">{r.provider}</Badge>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.latency_ms}ms</span>
          <span>{r.prompt_tokens}→{r.completion_tokens}</span>
          <span className="text-emerald-300 font-mono font-semibold">{fmtCost(r.cost_usd)}</span>
        </div>
      </div>
      <CardContent className="p-4">
        {errored ? (
          <div className="flex gap-2 text-sm text-rose-300">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="font-mono text-[12px]">{r.error}</span>
          </div>
        ) : (
          <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono leading-relaxed">{r.output}</pre>
        )}
      </CardContent>
    </Card>
  );
};

const PromptPlayground = () => {
  const [models, setModels] = useState([]);
  const [selected, setSelected] = useState(['gpt-5.2', 'claude-sonnet-4-6', 'gemini-3.1-pro-preview']);
  const [systemMsg, setSystemMsg] = useState('You are a precise, helpful assistant. Be concise.');
  const [userMsg, setUserMsg] = useState('Explain the difference between a vector database and a traditional database in one paragraph.');
  const [temperature, setTemperature] = useState([0.7]);
  const [maxTokens, setMaxTokens] = useState([512]);
  const [running, setRunning] = useState(false);
  const [out, setOut] = useState(null);

  useEffect(() => {
    axios.get(`${API}/prompts/models`).then(({ data }) => setModels(data.models || [])).catch(() => {});
  }, []);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const grouped = useMemo(() => {
    const g = { openai: [], anthropic: [], gemini: [] };
    models.forEach((m) => { (g[m.provider] = g[m.provider] || []).push(m); });
    return g;
  }, [models]);

  const totalCost = useMemo(
    () => (out?.results || []).reduce((a, r) => a + (r.cost_usd || 0), 0),
    [out],
  );

  const run = async () => {
    if (userMsg.trim().length < 3) { toast.error('Add a user prompt.'); return; }
    if (!selected.length) { toast.error('Pick at least 1 model.'); return; }
    if (selected.length > 3 && !localStorage.getItem('token')) {
      toast.error('Anonymous runs are capped at 3 models. Sign in for up to 6.');
      return;
    }
    setRunning(true);
    setOut(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/prompts/playground/run`,
        {
          system_message: systemMsg,
          user_message: userMsg,
          models: selected,
          temperature: temperature[0],
          max_tokens: maxTokens[0],
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {},
      );
      setOut(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed. Try fewer models or shorter prompt.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="prompt-playground-page">
      <SEOHead
        title="LLM Prompt Playground · Multi-Model Side-by-Side"
        description="Test the same prompt against GPT-5.2, Claude Sonnet 4.6, Gemini 3 Pro and 11 more models in parallel. See real latency, tokens and cost. Free."
        canonical="https://cartieai.com/prompt-playground"
        keywords="prompt playground, llm playground, gpt-5 vs claude vs gemini, multi-model prompt testing, llm cost comparison"
      />

      <nav className="sticky top-0 z-50 backdrop-blur-md bg-slate-950 text-slate-100 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white" data-testid="playground-nav-back">
            <ArrowLeft className="w-4 h-4" /> CARTIEAI
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/free-tools" className="text-sm text-slate-400 hover:text-white">More free tools</Link>
            <Link to="/register">
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="playground-nav-signup">
                Sign up to save prompts <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="space-y-3 mb-10">
          <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold">
            <Beaker className="w-3 h-3 mr-1.5 inline" /> Free · No signup · Real LLM calls
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight" data-testid="playground-h1">
            Prompt Playground
            <span className="block text-emerald-400 mt-1 text-3xl sm:text-4xl">Same prompt. Every flagship model. One screen.</span>
          </h1>
          <p className="text-slate-400 max-w-3xl leading-relaxed">
            Stop tab-hopping between 3 playgrounds. Fan a single prompt out to GPT-5.2, Claude Sonnet 4.6 and Gemini 3 Pro in parallel — see latency, token usage, and USD cost side by side.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6">
          {/* LEFT: editor */}
          <Card className="bg-slate-900 text-slate-100 border-slate-800">
            <CardContent className="p-5 space-y-5">
              <div>
                <label className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-bold">System message</label>
                <Textarea
                  value={systemMsg}
                  onChange={(e) => setSystemMsg(e.target.value)}
                  className="mt-1.5 bg-slate-950 border-slate-800 text-slate-100 min-h-[80px] font-mono text-sm"
                  data-testid="playground-system-input"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-bold">User message</label>
                <Textarea
                  value={userMsg}
                  onChange={(e) => setUserMsg(e.target.value)}
                  className="mt-1.5 bg-slate-950 border-slate-800 text-slate-100 min-h-[140px] font-mono text-sm"
                  data-testid="playground-user-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">Tip: use <code className="text-emerald-300">{'{var}'}</code> or <code className="text-emerald-300">{'{{var}}'}</code> placeholders to template.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex justify-between items-center">
                    <label className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-bold">Temperature</label>
                    <span className="text-xs text-emerald-300 font-mono">{temperature[0].toFixed(2)}</span>
                  </div>
                  <Slider value={temperature} onValueChange={setTemperature} max={2} step={0.1} className="mt-2" data-testid="playground-temp-slider" />
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <label className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-bold">Max tokens</label>
                    <span className="text-xs text-emerald-300 font-mono">{maxTokens[0]}</span>
                  </div>
                  <Slider value={maxTokens} onValueChange={setMaxTokens} min={64} max={4096} step={64} className="mt-2" data-testid="playground-tokens-slider" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-bold">
                  Models · {selected.length} selected {selected.length > 3 && !localStorage.getItem('token') && <span className="text-amber-400 normal-case ml-1">(sign in for &gt;3)</span>}
                </label>
                {['openai', 'anthropic', 'gemini'].map((p) => (
                  <div key={p}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 mt-2">{p}</div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {(grouped[p] || []).map((m) => (
                        <ModelChip key={m.id} model={m} selected={selected.includes(m.id)} onToggle={toggle} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <Button
                onClick={run}
                disabled={running}
                size="lg"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold"
                data-testid="playground-run-btn"
              >
                {running ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running on {selected.length} model{selected.length > 1 ? 's' : ''}…</>
                ) : (
                  <><Play className="w-4 h-4 mr-2" /> Run on {selected.length} model{selected.length > 1 ? 's' : ''}</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* RIGHT: results */}
          <div className="space-y-4" data-testid="playground-results">
            {!out && !running && (
              <Card className="bg-slate-900 text-slate-100/30 border-slate-800/60 border-dashed">
                <CardContent className="p-12 text-center text-slate-500">
                  <Sparkles className="w-8 h-8 mx-auto mb-3 text-slate-400" />
                  <p className="text-sm">Hit <span className="text-emerald-300 font-semibold">Run</span> to see all {selected.length} models respond in parallel.</p>
                </CardContent>
              </Card>
            )}

            {out && (
              <>
                <Card className="bg-emerald-500/5 border-emerald-500/30">
                  <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1.5 text-slate-600"><Zap className="w-3.5 h-3.5 text-emerald-400" /> Wall: <span className="font-mono text-emerald-300">{out.duration_ms}ms</span></span>
                      <span className="flex items-center gap-1.5 text-slate-600"><Cpu className="w-3.5 h-3.5 text-emerald-400" /> Models: <span className="font-mono text-emerald-300">{out.model_count}</span></span>
                      <span className="flex items-center gap-1.5 text-slate-600"><DollarSign className="w-3.5 h-3.5 text-emerald-400" /> Total: <span className="font-mono text-emerald-300">{fmtCost(totalCost)}</span></span>
                    </div>
                    <Link to="/register" className="ml-auto">
                      <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="playground-save-cta">
                        <Bookmark className="w-3.5 h-3.5 mr-1.5" /> Save this prompt
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
                {(out.results || []).map((r, i) => <ResultCard key={`${r.model}-${i}`} r={r} />)}
              </>
            )}
          </div>
        </div>

        <Card className="mt-12 bg-slate-900 text-slate-100 border-emerald-500/30">
          <CardContent className="p-8 sm:p-10 text-center space-y-3">
            <h3 className="text-2xl font-bold text-white">Like this? Save prompts, version them, and grade outputs at scale.</h3>
            <p className="text-slate-400 text-sm max-w-2xl mx-auto">
              The full LLM Engineering Suite — versioned prompt library, production trace capture, dataset evals and A/B compare — comes with any paid plan.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
              <Link to="/pricing"><Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="playground-pricing-cta">See pricing <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/prompts"><Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="playground-library-cta">Open Prompt Library</Button></Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PromptPlayground;
